import React, { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Pencil,
  Bold,
  Heading1,
  Heading2,
  List,
  Save,
  X,
  Loader2,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import blogService from '../../services/blogService';
import ImageGallery from '../../components/ImageGallery';
import '../../styles/admin/Blogs.css';
import { sanitizeHtmlForRender } from '../../utils/sanitizeHtml';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');
const emptyBlog = { title: '', content: '', image_url: '', status: 'draft' };

const formatDate = (date) => {
  if (!date) return 'Not published';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyBlog);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const editorRef = useRef(null);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const data = await blogService.getAdminBlogs();
      setBlogs(data || []);
    } catch (error) {
      console.error('Failed to load blogs', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    // contentEditable doesn't bind to state automatically.
    // Rehydrate editor HTML whenever the modal opens for create/edit.
    if (!panelOpen || !editorRef.current) return;
    editorRef.current.innerHTML = form.content || '';
  }, [panelOpen, editingId]);

  const setEditorContent = (html) => {
    if (editorRef.current) {
      editorRef.current.innerHTML = html || '';
    }
    setForm((prev) => ({ ...prev, content: html || '' }));
  };

  const openNewBlog = () => {
    setEditingId(null);
    setForm(emptyBlog);
    setEditorContent('');
    setPanelOpen(true);
  };

  const openEditBlog = (blog) => {
    setEditingId(blog.id);
    setForm({
      title: blog.title || '',
      image_url: blog.image_url || '',
      status: blog.status || 'draft',
      content: blog.content || '',
    });
    setEditorContent(blog.content || '');
    setPanelOpen(true);
  };

  const handleToolbar = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    const html = editorRef.current.innerHTML;
    setForm((prev) => ({ ...prev, content: html }));
  };

  const handleList = () => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand('insertUnorderedList', false, null);
    setForm((prev) => ({ ...prev, content: editorRef.current.innerHTML }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert('Please add a title');
      return;
    }

    const html = (editorRef.current?.innerHTML || '').trim();
    if (!html) {
      alert('Write some content before saving');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        image_url: (form.image_url || '').trim(),
        content: html,
        status: form.status,
      };

      if (editingId) {
        await blogService.updateBlog(editingId, payload);
      } else {
        await blogService.createBlog(payload);
      }

      setPanelOpen(false);
      setForm(emptyBlog);
      setEditorContent('');
      await loadBlogs();
    } catch (error) {
      console.error('Failed to save blog', error);
      alert('Saving blog failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    setEditingId(null);
    setForm(emptyBlog);
    setEditorContent('');
  };

  const renderStatusBadge = (status, publishedAt) => {
    if (status === 'published') {
      return (
        <span className="badge badge-live">
          <CheckCircle2 size={14} />
          Live · {formatDate(publishedAt)}
        </span>
      );
    }
    return (
      <span className="badge badge-draft">
        <Clock size={14} />
        Draft
      </span>
    );
  };

  const plainPreview = (html) =>
    (html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 140);

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  return (
    <div className="admin-blogs">
      <div className="admin-blogs-header">
        <div>
          <p className="eyebrow">Content</p>
          <h1>Blogs</h1>
          <p className="sub">Publish and edit articles without images.</p>
        </div>
        <button className="btn-primary" onClick={openNewBlog}>
          <Plus size={16} />
          New Blog
        </button>
      </div>

      <div className="admin-blogs-grid">
        <section className="blog-list">
          <div className="blog-list-head">
            <p>{blogs.length} blog{blogs.length === 1 ? '' : 's'}</p>
          </div>
          {loading ? (
            <div className="loader-row">
              <Loader2 className="spin" size={20} />
              Loading blogs...
            </div>
          ) : blogs.length === 0 ? (
            <div className="empty-state">
              <p>No blogs yet.</p>
              <button className="btn-ghost" onClick={openNewBlog}>
                <Plus size={14} />
                Create your first post
              </button>
            </div>
          ) : (
            <ul className="blog-items">
              {blogs.map((blog) => (
                <li key={blog.id} className="blog-item" onClick={() => openEditBlog(blog)}>
                  <div className="blog-item-main">
                    <div>
                      <p className="blog-title">
                        {typeof blog.title === 'string' ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(blog.title) }} />
                        ) : (
                          blog.title
                        )}
                      </p>
                      {blog.image_url ? (
                        <div className="blog-post-header-image-wrap">
                          <img src={getImageUrl(blog.image_url)} alt={blog.title} className="blog-post-header-image" />
                        </div>
                      ) : null}
                      <p className="blog-preview">
                        {typeof plainPreview(blog.content) === 'string' ? (
                          <div dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRender(plainPreview(blog.content)) }} />
                        ) : (
                          plainPreview(blog.content)
                        )}
                      </p>
                    </div>
                    {renderStatusBadge(blog.status, blog.published_at)}
                  </div>
                  <div className="blog-meta">
                    <span>Updated {formatDate(blog.updated_at || blog.created_at)}</span>
                    <button className="icon-btn" type="button" onClick={() => openEditBlog(blog)}>
                      <Pencil size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {panelOpen && (
          <div className="blog-modal-overlay" onClick={closePanel}>
            <div className="blog-modal" onClick={(e) => e.stopPropagation()}>
              <div className="editor-head">
                <div>
                  <p className="eyebrow">{editingId ? 'Edit blog' : 'New blog'}</p>
                  <h3>{editingId ? 'Update content' : 'Write and publish'}</h3>
                </div>
                <button className="icon-btn" type="button" onClick={closePanel}>
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <label className="field">
                  <span>Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter a headline"
                  />
                </label>

                <label className="field">
                  <span>Status</span>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Publish now</option>
                  </select>
                </label>

                <label className="field">
                  <span>Blog Image</span>
                  <div className="blog-image-input-row">
                    <input
                      type="text"
                      value={form.image_url}
                      onChange={(e) => setForm((prev) => ({ ...prev, image_url: e.target.value }))}
                      placeholder="/uploads/image.jpg or https://..."
                    />
                    <button type="button" className="btn-ghost" onClick={() => setGalleryOpen(true)}>
                      Choose Image
                    </button>
                  </div>
                  {form.image_url ? (
                    <img
                      src={getImageUrl(form.image_url)}
                      alt="Blog preview"
                      className="blog-image-preview"
                    />
                  ) : null}
                </label>

                <div className="editor-card">
                  <div className="toolbar">
                    <button type="button" onClick={() => handleToolbar('bold')} title="Bold">
                      <Bold size={16} />
                    </button>
                    <button type="button" onClick={() => handleToolbar('formatBlock', 'h1')} title="Heading 1">
                      <Heading1 size={16} />
                    </button>
                    <button type="button" onClick={() => handleToolbar('formatBlock', 'h2')} title="Heading 2">
                      <Heading2 size={16} />
                    </button>
                    <button type="button" onClick={handleList} title="Bullet list">
                      <List size={16} />
                    </button>
                  </div>
                  <div
                    className="editor-area"
                    contentEditable
                    ref={editorRef}
                    onInput={() => setForm((prev) => ({ ...prev, content: editorRef.current?.innerHTML || '' }))}
                    placeholder="Write your blog content. Use the toolbar for bold text and headings. Images are disabled."
                    suppressContentEditableWarning
                  />
                </div>
              </div>

              <button className="btn-primary full" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="spin" size={16} /> : <Save size={16} />}
                {saving ? 'Saving...' : editingId ? 'Update blog' : 'Publish blog'}
              </button>
            </div>
          </div>
        )}
      </div>
      <ImageGallery
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
        currentImage={form.image_url}
      />
    </div>
  );
}
